import { useState, useEffect } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { getOffsetPageParam, unwrap } from '@billda/api';
import { api } from '../../../shared/api/billdaApi';
import type { PlotListItem } from '@billda/api';
import { useAuthStore } from '../../auth/stores/authStore';

export const plotsKeys = {
  all: ['plots'] as const,
  lists: () => [...plotsKeys.all, 'list'] as const,
  list: (siteId: number, search: string, isAuthenticated: boolean) =>
    [...plotsKeys.lists(), { siteId, search, isAuthenticated }] as const,
};

const ITEMS_PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export interface UsePlotsQueryReturn {
  plots: PlotListItem[];
  totalCount: number;
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: Error | null;
  search: string;
  loadMore: () => void;
}

/**
 * Hook to fetch plots for a site with search and pagination support.
 * Uses the v2 API with offset-based pagination.
 */
export function usePlotsQuery(
  siteId: number,
  search: string = ''
): UsePlotsQueryReturn {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);

  const [debouncedSearch, setDebouncedSearch] = useState(search);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [search]);

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: plotsKeys.list(siteId, debouncedSearch, isAuthenticated),
    queryFn: async ({ pageParam, signal }) =>
      unwrap(
        await api.legacy.GET('/api/v2/sites/{siteId}/plots', {
          params: {
            path: { siteId },
            query: {
              offset: pageParam,
              take: ITEMS_PER_PAGE,
              search: debouncedSearch || undefined,
            },
          },
          signal,
        })
      ),
    getNextPageParam: getOffsetPageParam,
    initialPageParam: 0,
    enabled: isAuthenticated && siteId > 0,
    staleTime: 30000,
    placeholderData: previousData => previousData,
  });

  const plots = data?.pages.flatMap(page => page.items) ?? [];
  const totalCount = data?.pages[0]?.totalCount ?? 0;

  return {
    plots,
    totalCount,
    hasMore: hasNextPage ?? false,
    isLoading,
    isLoadingMore: isFetchingNextPage,
    error: error as Error | null,
    search: debouncedSearch,
    loadMore: fetchNextPage,
  };
}
